import { Box, Flex } from '@chakra-ui/react';
import { Popover, Tag, Text } from '@kula-ai/components';
import type { CandidateSummary } from '../../../types';

type ChipVariant = 'neutral' | 'success' | 'warning';

// A single pill: its label, tone, and (optionally) the hover info-card that
// explains what the tag means — the DQA ask was "these tags are confusing,
// give the info on hover in card format".
interface Chip {
  id: string;
  label: string;
  variant: ChipVariant;
  hint?: { title: string; body: string };
}

const cap = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

const jobsPhrase = (n: number): string => `${n} ${n === 1 ? 'job' : 'jobs'}`;

// Derive the pill row from a candidate. Explicit `tags` (backend-authored)
// render plain — we don't know their semantics so there's nothing to explain.
// The derived match / status / job-count chips each carry a hover card.
const buildChips = (c: CandidateSummary): Chip[] => {
  if (c.tags.length > 0) {
    return c.tags.map((t, i) => ({
      id: `tag-${i}-${t}`,
      label: t,
      variant: 'neutral',
    }));
  }

  const chips: Chip[] = [];

  if (c.matchBadge) {
    const pct = c.matchScore ? Math.round(c.matchScore.value * 100) : null;
    chips.push({
      id: 'match',
      label: c.matchBadge,
      variant: /exact|strong/i.test(c.matchBadge) ? 'success' : 'neutral',
      hint: {
        title: c.matchBadge,
        body:
          pct !== null
            ? `AI relevance ${pct}% — how closely this candidate matches your search criteria.`
            : 'How closely this candidate matches your search criteria.',
      },
    });
  }

  // Engagement badges cover status/activity when present, so skip the derived
  // status + job-count chips to avoid duplicating the same signal.
  if (c.badges && c.badges.length > 0) return chips;

  Array.from(new Set(c.applicationStatuses ?? []))
    .map(cap)
    .forEach((status) =>
      chips.push({
        id: `status-${status}`,
        label: status,
        variant: 'neutral',
        hint: {
          title: 'Application status',
          body: c.hasActiveApplication
            ? `Currently active in the pipeline${
                c.jobCount ? ` across ${jobsPhrase(c.jobCount)}` : ''
              }. Stage: ${status}.`
            : `Pipeline stage: ${status}.`,
        },
      })
    );

  if (typeof c.jobCount === 'number' && c.jobCount > 0) {
    chips.push({
      id: 'jobs',
      label: `On ${jobsPhrase(c.jobCount)}`,
      variant: 'neutral',
      hint: {
        title: 'Job attachments',
        body: `Attached to ${jobsPhrase(
          c.jobCount
        )} across your pipeline. Open the profile to see which.`,
      },
    });
  }

  return chips;
};

// Nexus engagement badge tone → kula-ai Tag variant.
const TONE_TO_VARIANT: Record<string, ChipVariant> = {
  positive: 'success',
  warning: 'warning',
  info: 'neutral',
};

const HintCard = ({ title, body }: { title: string; body: string }) => (
  <Box maxW="240px" p="2px">
    <Text variant="label" as="p" className="text-secondary-900">
      {title}
    </Text>
    <Text variant="small" as="p" className="text-secondary-600 mt-1">
      {body}
    </Text>
  </Box>
);

export const CandidateTags = ({
  candidate,
}: {
  candidate: CandidateSummary;
}) => {
  const chips = buildChips(candidate);
  const badges = candidate.badges ?? [];
  if (chips.length === 0 && badges.length === 0) return null;

  return (
    <Flex
      wrap="wrap"
      gap="6px"
      mt="10px"
      data-test-id="agentforge-candidate-tags"
    >
      {chips.map((chip) => {
        const tag = <Tag variant={chip.variant} size="sm" label={chip.label} />;
        if (!chip.hint) {
          return (
            <Box as="span" key={chip.id}>
              {tag}
            </Box>
          );
        }
        return (
          <Popover
            key={chip.id}
            openOn="hover"
            hoverDelay={150}
            side="top"
            trigger={
              <Box as="span" cursor="default">
                {tag}
              </Box>
            }
          >
            <HintCard title={chip.hint.title} body={chip.hint.body} />
          </Popover>
        );
      })}
      {badges.map((b) => (
        <Tag
          key={b.key || b.label}
          size="sm"
          variant={TONE_TO_VARIANT[b.tone ?? ''] ?? 'neutral'}
          label={b.label}
        />
      ))}
    </Flex>
  );
};
